import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, IconButton, Button, Chip, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    ArrowBackOutlined, NorthEastOutlined,
    AccountBalanceOutlined, CreditCardOutlined,
    CheckCircleOutline, LockOutlined,
    PhoneOutlined, QrCodeOutlined,
    ContentCopyOutlined, CheckCircle,
} from '@mui/icons-material';

const B = {
    deep:    '#0D0820',
    dark:    '#1A0F3D',
    mid:     '#2D1B69',
    purple:  '#7B5EA7',
    light:   '#9B7EC8',
    accent:  '#C8B8E8',
    lavender:'#F7F4FC',
    ink:     '#0D0A1A',
    green:   '#00C896',
    greenDim:'#00C89618',
    amber:   '#FF9500',
    blue:    '#007AFF',
    red:     '#FF4D6A',
};

const QUICK_AMOUNTS = [1000, 5000, 10000, 20000, 50000];

const PAYMENT_METHODS = [
    { id: 'bank_transfer', label: 'Bank Transfer',       sub: 'Instant · Free',          Icon: AccountBalanceOutlined, color: B.green,  charge: 0     },
    { id: 'card',          label: 'Debit / Credit Card', sub: '1.5% processing fee',     Icon: CreditCardOutlined,     color: B.purple, charge: 0.015 },
    { id: 'ussd',          label: 'Bank USSD',           sub: 'Works without internet',  Icon: PhoneOutlined,          color: B.amber,  charge: 0     },
    { id: 'qr',            label: 'Scan QR Code',        sub: 'Any banking app',         Icon: QrCodeOutlined,         color: B.blue,   charge: 0     },
];

const BANKS_FOR_TRANSFER = ['Access Bank', 'GTBank', 'First Bank', 'Zenith Bank', 'UBA', 'Kuda Bank', 'Opay', 'Palmpay'];

const USSD_CODES: Record<string, string> = {
    'GTBank':        '*737*58*{AMOUNT}*{ACCT}#',
    'Access Bank':   '*901*58*{AMOUNT}*{ACCT}#',
    'First Bank':    '*894*58*{AMOUNT}*{ACCT}#',
    'Zenith Bank':   '*966*58*{AMOUNT}*{ACCT}#',
    'UBA':           '*919*58*{AMOUNT}*{ACCT}#',
    'Kuda Bank':     '*5573*58*{AMOUNT}*{ACCT}#',
    'Sterling Bank': '*822*58*{AMOUNT}*{ACCT}#',
    'Stanbic IBTC':  '*909*58*{AMOUNT}*{ACCT}#',
};

const NG_ACCOUNT = '7823456109';
const NG_BANK    = 'NG Credit MFB';

const fmt = (n: number) =>
    new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(n);

const FundWalletPage: React.FC = () => {
    const navigate = useNavigate();
    const theme    = useTheme();
    const isDark   = theme.palette.mode === 'dark';

    const [amount,       setAmount]       = useState('');
    const [method,       setMethod]       = useState('bank_transfer');
    const [step,         setStep]         = useState<'amount' | 'detail' | 'success'>('amount');
    const [loading,      setLoading]      = useState(false);
    const [copied,       setCopied]       = useState<string | null>(null);
    const [transferBank, setTransferBank] = useState('');
    const [ussdBank,     setUssdBank]     = useState('');

    const numAmount = parseFloat(amount) || 0;
    const selMethod = PAYMENT_METHODS.find(m => m.id === method)!;
    const charge    = selMethod.charge > 0 && selMethod.charge < 1 ? numAmount * selMethod.charge : selMethod.charge;
    const total     = numAmount + charge;

    // ── Theme-aware design tokens ──
    const pageBg     = isDark ? B.deep : B.lavender;
    const surfaceBg  = isDark ? alpha(B.mid, .55)                  : '#fff';
    const surfaceBdr = `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`;
    const surfaceShd = `0 4px 28px ${alpha(B.dark, isDark ? .25 : .06)}`;
    const textPri    = isDark ? '#fff' : B.ink;
    const textMid    = alpha(isDark ? '#fff' : B.ink, .45);
    const textFaint  = alpha(isDark ? '#fff' : B.ink, .28);
    const dividerC   = alpha(isDark ? '#fff' : B.dark, .08);

    const handleCopy = (text: string, key: string) => {
        navigator.clipboard.writeText(text);
        setCopied(key);
        setTimeout(() => setCopied(null), 2000);
    };

    const canContinueDetail =
        method === 'bank_transfer' ? !!transferBank :
            method === 'ussd'          ? !!ussdBank     : true;

    const handleCTA = () => {
        if (step === 'amount') { setStep('detail'); return; }
        setLoading(true);
        setTimeout(() => { setLoading(false); setStep('success'); }, 1800);
    };

    /* ── Reusable themed card ── */
    const Card: React.FC<{ children: React.ReactNode; sx?: object }> = ({ children, sx = {} }) => (
        <Box sx={{ background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd, borderRadius: '20px', backdropFilter: isDark ? 'blur(20px)' : 'none', ...sx }}>
            {children}
        </Box>
    );

    const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
        <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.18em', textTransform: 'uppercase', mb: 2, fontWeight: 600 }}>
            {children}
        </Typography>
    );

    const CopyRow: React.FC<{ label: string; value: string; copyKey: string }> = ({ label, value, copyKey }) => (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderRadius: '12px', background: alpha(isDark ? '#fff' : B.dark, .04), border: `1px solid ${dividerC}`, mb: 1 }}>
            <Box>
                <Typography sx={{ color: textFaint, fontSize: '.62rem', mb: .2 }}>{label}</Typography>
                <Typography sx={{ color: textPri, fontWeight: 600, fontSize: '.83rem', letterSpacing: copyKey === 'acct' ? '.1em' : 'normal' }}>{value}</Typography>
            </Box>
            <IconButton onClick={() => handleCopy(value, copyKey)} sx={{ p: .6, color: textFaint, '&:hover': { color: textPri } }}>
                {copied === copyKey
                    ? <CheckCircle sx={{ fontSize: 15, color: B.green }} />
                    : <ContentCopyOutlined sx={{ fontSize: 15 }} />}
            </IconButton>
        </Box>
    );

    /* ── SUCCESS ── */
    if (step === 'success') return (
        <Box sx={{ minHeight: '100vh', background: isDark ? `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)` : B.lavender, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 3 }}>
            <Box sx={{ textAlign: 'center', maxWidth: 340 }}>
                <Box sx={{ width: 88, height: 88, borderRadius: '50%', background: B.greenDim, border: `2px solid ${B.green}`, boxShadow: `0 0 40px ${alpha(B.green, .22)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
                    <CheckCircleOutline sx={{ color: B.green, fontSize: 44 }} />
                </Box>
                <Typography sx={{ color: isDark ? '#fff' : B.ink, fontFamily: "'Instrument Serif', serif", fontSize: '2rem', mb: .75 }}>
                    {method === 'ussd' || method === 'qr' ? 'Request Initiated!' : 'Wallet Funded!'}
                </Typography>
                <Typography sx={{ color: textMid, fontSize: '.85rem', mb: .5 }}>{fmt(total)} via {selMethod.label}</Typography>
                <Typography sx={{ color: method === 'ussd' || method === 'qr' ? textFaint : B.green, fontSize: '.76rem', fontWeight: 600, mb: 4 }}>
                    {method === 'ussd' || method === 'qr' ? 'Your wallet will credit once payment confirms.' : 'Balance updated instantly ✓'}
                </Typography>
                <Button onClick={() => navigate('/wallet')} variant="contained" sx={{ background: `linear-gradient(135deg, ${B.purple}, ${B.light})`, borderRadius: 50, px: 5, py: 1.5, textTransform: 'none', fontWeight: 700, fontSize: '.9rem', boxShadow: `0 12px 32px ${alpha(B.purple, .45)}` }}>
                    Back to Wallet
                </Button>
            </Box>
        </Box>
    );

    /* ── Method detail panels ── */

    const BankTransferPanel = (
        <>
            <Card sx={{ p: 2.5, mb: 2 }}>
                <Label>Transfer to this account</Label>
                <CopyRow label="Account Name"   value="Ahmed Bello — NG Credit" copyKey="name" />
                <CopyRow label="Account Number" value={NG_ACCOUNT}               copyKey="acct" />
                <CopyRow label="Bank"           value={NG_BANK}                  copyKey="bank" />
                <Box sx={{ mt: 1.5, p: 1.5, borderRadius: '12px', background: alpha(B.green, .07), border: `1px solid ${alpha(B.green, .2)}` }}>
                    <Typography sx={{ color: B.green, fontSize: '.72rem', fontWeight: 600 }}>
                        Transfer exactly <strong>{fmt(total)}</strong> — your wallet credits instantly.
                    </Typography>
                </Box>
            </Card>
            <Card sx={{ p: 2.5, mb: 2.5 }}>
                <Label>Which bank are you sending from?</Label>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
                    {BANKS_FOR_TRANSFER.map(b => (
                        <Box key={b} onClick={() => setTransferBank(b)} sx={{
                            p: 1.25, borderRadius: '12px', cursor: 'pointer', textAlign: 'center',
                            background: transferBank === b ? alpha(B.green, .1) : alpha(isDark ? '#fff' : B.dark, .03),
                            border: `1px solid ${transferBank === b ? alpha(B.green, .4) : dividerC}`,
                            transition: 'all .18s',
                        }}>
                            <Typography sx={{ color: transferBank === b ? B.green : textMid, fontSize: '.73rem', fontWeight: 600 }}>{b}</Typography>
                        </Box>
                    ))}
                </Box>
            </Card>
        </>
    );

    const CardPanel = (
        <Card sx={{ p: 2.5, mb: 2.5 }}>
            <Label>Card details</Label>
            {[
                { label: 'Card Number', placeholder: '1234  5678  9012  3456', maxLen: 19 },
                { label: 'Expiry Date', placeholder: 'MM / YY',               maxLen: 7  },
                { label: 'CVV',         placeholder: '···',                    maxLen: 3  },
            ].map(f => (
                <Box key={f.label} sx={{ mb: 1.75 }}>
                    <Typography sx={{ color: textFaint, fontSize: '.62rem', letterSpacing: '.1em', textTransform: 'uppercase', mb: .7 }}>{f.label}</Typography>
                    <Box component="input" placeholder={f.placeholder} maxLength={f.maxLen} sx={{
                        width: '100%', p: '14px 16px', borderRadius: '12px',
                        background: alpha(isDark ? '#fff' : B.dark, .04),
                        border: `1px solid ${dividerC}`,
                        color: textPri, fontSize: '.9rem', outline: 'none',
                        fontFamily: "'DM Sans', sans-serif", letterSpacing: '.06em',
                        '&::placeholder': { color: textFaint },
                        '&:focus': { borderColor: alpha(B.purple, .5), background: alpha(B.purple, .04) },
                        transition: 'all .2s', boxSizing: 'border-box',
                    }} />
                </Box>
            ))}
            <Box sx={{ p: 1.5, borderRadius: '12px', background: alpha(B.purple, .06), border: `1px solid ${alpha(B.purple, .15)}` }}>
                <Typography sx={{ color: textMid, fontSize: '.7rem' }}>
                    Fee: <strong style={{ color: textPri }}>{fmt(charge)}</strong> · Total charged: <strong style={{ color: textPri }}>{fmt(total)}</strong>
                </Typography>
            </Box>
        </Card>
    );

    const UssdPanel = (() => {
        const code = ussdBank
            ? USSD_CODES[ussdBank].replace('{AMOUNT}', String(numAmount)).replace('{ACCT}', NG_ACCOUNT)
            : '';
        return (
            <>
                <Card sx={{ p: 2.5, mb: 2 }}>
                    <Label>Select your bank</Label>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
                        {Object.keys(USSD_CODES).map(b => (
                            <Box key={b} onClick={() => setUssdBank(b)} sx={{
                                p: 1.25, borderRadius: '12px', cursor: 'pointer', textAlign: 'center',
                                background: ussdBank === b ? alpha(B.amber, .1) : alpha(isDark ? '#fff' : B.dark, .03),
                                border: `1px solid ${ussdBank === b ? alpha(B.amber, .4) : dividerC}`,
                                transition: 'all .18s',
                            }}>
                                <Typography sx={{ color: ussdBank === b ? B.amber : textMid, fontSize: '.73rem', fontWeight: 600 }}>{b}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Card>
                {ussdBank && (
                    <Card sx={{ p: 2.5, mb: 2.5 }}>
                        <Label>Dial this code</Label>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderRadius: '14px', background: alpha(B.amber, .08), border: `1px solid ${alpha(B.amber, .25)}`, mb: 1.5 }}>
                            <Typography sx={{ color: B.amber, fontWeight: 700, fontSize: '.95rem', letterSpacing: '.04em', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                {code}
                            </Typography>
                            <IconButton onClick={() => handleCopy(code, 'ussd')} sx={{ p: .6, color: textFaint, '&:hover': { color: textPri }, flexShrink: 0, ml: 1 }}>
                                {copied === 'ussd' ? <CheckCircle sx={{ fontSize: 16, color: B.green }} /> : <ContentCopyOutlined sx={{ fontSize: 16 }} />}
                            </IconButton>
                        </Box>
                        <Typography sx={{ color: textMid, fontSize: '.7rem', lineHeight: 1.6 }}>
                            Dial from your <strong style={{ color: textPri }}>{ussdBank}</strong> registered line. Your wallet credits automatically once the transfer completes.
                        </Typography>
                    </Card>
                )}
            </>
        );
    })();

    const QR_CELLS = [
        1,1,1,1,1,1,1, 0,1,0,0,0,0,0,
        1,0,1,0,1,0,1, 0,0,1,0,1,0,0,
        1,0,1,0,1,0,1, 0,1,0,1,0,0,1,
        1,0,1,0,1,0,1, 0,0,0,1,0,1,0,
        1,0,1,0,1,0,1, 0,1,0,0,0,0,1,
        1,0,0,0,0,0,1, 0,0,1,0,1,0,0,
        1,1,1,1,1,1,1, 0,1,0,0,0,1,0,
        0,0,1,0,0,1,0, 1,0,1,0,1,0,1,
        0,1,1,0,1,1,0, 0,0,0,1,0,0,1,
        1,0,0,1,0,0,1, 0,1,0,1,0,1,0,
        0,1,0,1,0,1,0, 1,0,1,0,0,0,1,
        1,0,1,0,1,0,0, 0,1,0,1,0,1,0,
        0,0,1,1,0,1,0, 1,0,0,1,0,0,0,
        0,0,0,0,0,0,1, 0,1,0,1,0,1,0,
        1,1,1,1,1,1,1, 0,0,1,0,0,1,0,
    ];

    const QrPanel = (
        <Card sx={{ p: 3, mb: 2.5, textAlign: 'center' }}>
            <Label>Scan to Pay</Label>
            <Box sx={{ width: 200, height: 200, mx: 'auto', mb: 2.5, borderRadius: '20px', p: '18px', background: '#fff', boxShadow: `0 8px 40px ${alpha(B.blue, .2)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(14, 1fr)', gap: '2.5px', width: '100%' }}>
                    {QR_CELLS.map((cell, i) => (
                        <Box key={i} sx={{ borderRadius: '1.5px', background: cell ? B.deep : 'transparent', aspectRatio: '1' }} />
                    ))}
                </Box>
                <Box sx={{ position: 'absolute', width: 38, height: 38, borderRadius: '9px', background: `linear-gradient(135deg, ${B.purple}, ${B.light})`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 12px rgba(0,0,0,.25)' }}>
                    <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: '.72rem', letterSpacing: '-.02em' }}>NG</Typography>
                </Box>
            </Box>
            <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.92rem', mb: .5 }}>Scan with any bank app</Typography>
            <Typography sx={{ color: textMid, fontSize: '.75rem', mb: 2.5 }}>GTBank · Access · UBA · Kuda · Opay & more</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, borderRadius: '12px', background: alpha(isDark ? '#fff' : B.dark, .04), border: `1px solid ${dividerC}`, mb: 1 }}>
                <Typography sx={{ color: textMid, fontSize: '.72rem' }}>Account</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: .75 }}>
                    <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.75rem', letterSpacing: '.1em' }}>{NG_ACCOUNT}</Typography>
                    <IconButton onClick={() => handleCopy(NG_ACCOUNT, 'qr_acct')} sx={{ p: .4, color: textFaint, '&:hover': { color: textPri } }}>
                        {copied === 'qr_acct' ? <CheckCircle sx={{ fontSize: 13, color: B.green }} /> : <ContentCopyOutlined sx={{ fontSize: 13 }} />}
                    </IconButton>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, borderRadius: '12px', background: alpha(B.blue, .07), border: `1px solid ${alpha(B.blue, .2)}` }}>
                <Typography sx={{ color: textMid, fontSize: '.72rem' }}>Amount to send</Typography>
                <Typography sx={{ color: B.blue, fontWeight: 700, fontSize: '.75rem' }}>{fmt(numAmount)}</Typography>
            </Box>
        </Card>
    );

    const detailPanel: Record<string, React.ReactNode> = {
        bank_transfer: BankTransferPanel,
        card:          CardPanel,
        ussd:          UssdPanel,
        qr:            QrPanel,
    };

    const ctaBg = () => {
        if (step === 'detail') {
            if (method === 'ussd') return `linear-gradient(135deg, ${B.amber}, #FFB347)`;
            if (method === 'qr')   return `linear-gradient(135deg, ${B.blue}, #5AC8FA)`;
        }
        return `linear-gradient(135deg, ${B.purple} 0%, ${B.light} 100%)`;
    };
    const ctaLabel = () => {
        if (loading) return 'Processing…';
        if (step === 'amount') return `Continue · ${fmt(numAmount)}`;
        if (method === 'ussd') return "I've Dialled the Code";
        if (method === 'qr')   return "I've Completed the Transfer";
        return `Confirm · Fund ${fmt(total)}`;
    };
    const showCTA = (step === 'amount' && numAmount >= 100) || (step === 'detail' && canContinueDetail);

    return (
        <Box sx={{ minHeight: '100vh', background: pageBg, pb: 10, fontFamily: "'DM Sans', system-ui, sans-serif" }}>

            {/* ══ HERO HEADER ════════════════════════════════════════ */}
            <Box sx={{
                background: `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)`,
                pt: { xs: '80px', sm: '28px' },
                pb: { xs: 4, sm: 14 }, px: 3, position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -70, right: -50, width: 240, height: 240, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.light, .18)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: -10, left: -40, width: 200, height: 200, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.green, .1)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

                <Box display="flex" alignItems="center" gap={1.5}>
                    <IconButton onClick={() => step === 'detail' ? setStep('amount') : navigate(-1)}
                                sx={{ color: alpha('#fff', .7), background: alpha('#fff', .1), border: `1px solid ${alpha('#fff', .15)}`, width: 36, height: 36, '&:hover': { background: alpha('#fff', .2) } }}>
                        <ArrowBackOutlined sx={{ fontSize: 17 }} />
                    </IconButton>
                    <Box>
                        <Typography sx={{ color: alpha('#fff', .35), fontSize: '.62rem', letterSpacing: '.18em', textTransform: 'uppercase' }}>
                            {step === 'amount' ? 'Step 1 of 2 · Add Money' : `Step 2 of 2 · ${selMethod.label}`}
                        </Typography>
                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem', letterSpacing: '-.025em', fontFamily: "'Instrument Serif', serif" }}>
                            {step === 'amount' ? 'Fund Wallet' : 'Payment Details'}
                        </Typography>
                    </Box>
                    {step === 'detail' && (
                        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: .7, background: alpha(selMethod.color, .2), border: `1px solid ${alpha(selMethod.color, .4)}`, borderRadius: 20, px: 1.5, py: .5 }}>
                            <selMethod.Icon sx={{ fontSize: 13, color: selMethod.color }} />
                            <Typography sx={{ color: selMethod.color, fontSize: '.64rem', fontWeight: 700 }}>{selMethod.label.split(' ')[0]}</Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            <Container maxWidth="sm" sx={{ mt: { xs: 2, sm: -8 }, position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>

                {/* ── Amount summary pill (detail step) ── */}
                {step === 'detail' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, px: 2, py: 1.5, borderRadius: '16px', background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd }}>
                        <Box>
                            <Typography sx={{ color: textFaint, fontSize: '.62rem' }}>Funding amount</Typography>
                            <Typography sx={{ color: textPri, fontFamily: "'Instrument Serif', serif", fontSize: '1.5rem', lineHeight: 1.1 }}>{fmt(numAmount)}</Typography>
                        </Box>
                        {charge > 0 && (
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography sx={{ color: textFaint, fontSize: '.62rem' }}>Fee · Total</Typography>
                                <Typography sx={{ color: textMid, fontSize: '.82rem', fontWeight: 600 }}>
                                    {fmt(charge)} · <span style={{ color: textPri }}>{fmt(total)}</span>
                                </Typography>
                            </Box>
                        )}
                        <Button size="small" onClick={() => setStep('amount')} sx={{ color: B.purple, fontWeight: 700, fontSize: '.68rem', textTransform: 'none', minWidth: 0, px: 1.25, py: .4, borderRadius: 10, '&:hover': { background: alpha(B.purple, .08) } }}>
                            Edit
                        </Button>
                    </Box>
                )}

                {/* ── STEP: Amount ── */}
                {step === 'amount' && (
                    <>
                        <Card sx={{ p: 3, mb: 2.5 }}>
                            <Label>Enter Amount</Label>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography sx={{ color: textFaint, fontFamily: "'Instrument Serif', serif", fontSize: '2.2rem', lineHeight: 1 }}>₦</Typography>
                                <Box component="input" value={amount}
                                     onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAmount(e.target.value.replace(/\D/g, ''))}
                                     placeholder="0" autoFocus
                                     sx={{ background: 'transparent', border: 'none', outline: 'none', color: textPri, fontFamily: "'Instrument Serif', serif", fontSize: { xs: '2.8rem', sm: '3.4rem' }, fontWeight: 400, letterSpacing: '-.03em', width: '100%', '&::placeholder': { color: textFaint } }} />
                            </Box>
                            <Box sx={{ height: '1.5px', background: `linear-gradient(90deg, ${B.purple}, transparent)`, mb: 3 }} />
                            <Label>Quick Select</Label>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {QUICK_AMOUNTS.map(q => (
                                    <Chip key={q} label={`₦${q.toLocaleString()}`} onClick={() => setAmount(String(q))}
                                          sx={{ background: amount === String(q) ? alpha(B.purple, .15) : alpha(isDark ? '#fff' : B.dark, .05), border: `1px solid ${amount === String(q) ? B.purple : dividerC}`, color: amount === String(q) ? B.purple : textMid, fontWeight: 600, fontSize: '.72rem', cursor: 'pointer', '&:hover': { background: alpha(B.purple, .1) } }} />
                                ))}
                            </Box>
                        </Card>

                        <Card sx={{ p: 2.5, mb: 2.5 }}>
                            <Label>Payment Method</Label>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
                                {PAYMENT_METHODS.map(m => {
                                    const active = method === m.id;
                                    return (
                                        <Box key={m.id} onClick={() => setMethod(m.id)} sx={{
                                            p: 1.75, borderRadius: '16px', cursor: 'pointer',
                                            background: active ? alpha(m.color, .1) : alpha(isDark ? '#fff' : B.dark, .03),
                                            border: `1.5px solid ${active ? alpha(m.color, .4) : dividerC}`,
                                            transition: 'all .2s',
                                            '&:hover': { background: alpha(m.color, .08), border: `1.5px solid ${alpha(m.color, .25)}` },
                                        }}>
                                            <Box sx={{ width: 38, height: 38, borderRadius: '11px', background: active ? alpha(m.color, .15) : alpha(isDark ? '#fff' : B.dark, .06), display: 'flex', alignItems: 'center', justifyContent: 'center', color: active ? m.color : textMid, mb: 1.25, transition: 'all .2s' }}>
                                                <m.Icon sx={{ fontSize: 19 }} />
                                            </Box>
                                            <Typography sx={{ color: active ? textPri : textMid, fontWeight: 700, fontSize: '.75rem', lineHeight: 1.2, mb: .3 }}>{m.label}</Typography>
                                            <Typography sx={{ color: active ? textMid : textFaint, fontSize: '.64rem', lineHeight: 1.3 }}>{m.sub}</Typography>
                                            {active && <Box sx={{ mt: 1, width: 20, height: 3, borderRadius: 4, background: `linear-gradient(90deg, ${m.color}, ${alpha(m.color, .3)})` }} />}
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Card>
                    </>
                )}

                {/* ── STEP: Detail ── */}
                {step === 'detail' && detailPanel[method]}

                {/* ── CTA ── */}
                {showCTA && (
                    <Button fullWidth onClick={handleCTA} disabled={loading} sx={{
                        background: loading ? alpha(B.purple, .35) : ctaBg(),
                        color: '#fff', borderRadius: 50, py: 1.85, textTransform: 'none',
                        fontWeight: 700, fontSize: '.95rem', letterSpacing: '.01em',
                        boxShadow: `0 14px 36px ${alpha(B.purple, .3)}`,
                        transition: 'all .25s',
                        '&:hover:not(:disabled)': { transform: 'translateY(-2px)', boxShadow: `0 20px 44px ${alpha(B.purple, .45)}` },
                        '&:disabled': { color: alpha('#fff', .3) },
                    }} startIcon={loading ? undefined : <NorthEastOutlined />}>
                        {ctaLabel()}
                    </Button>
                )}

                <Box sx={{ mt: 3.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .75 }}>
                    <LockOutlined sx={{ fontSize: 11, color: alpha(isDark ? '#fff' : B.ink, .18) }} />
                    <Typography sx={{ fontSize: '.6rem', color: alpha(isDark ? '#fff' : B.ink, .18), letterSpacing: '.06em' }}>
                        256-BIT ENCRYPTION · NG CREDIT FINANCIAL SERVICES
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default FundWalletPage;